import { type AdsAttribution, parseAdsAttribution } from "@teksta/shared-types";
import { paidTouchFromUrl } from "./ads-attribution";

export const ATTRIBUTION_COOKIE = "t_attribution";
export const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
export const ATTRIBUTION_MAX_ENCODED_SIZE = 3800;
const CLICK_KEYS = ["gclid", "msclkid", "gbraid", "wbraid"] as const;
type Click = { id: string; at: number };
export type EntryLabels = { source?: string; campaign?: string; medium?: string };
export type AttributionStorage = {
  v: 1;
  gclid?: Click;
  msclkid?: Click;
  gbraid?: Click;
  wbraid?: Click;
  paid?: AdsAttribution;
  entry?: EntryLabels & { at: number };
};

function object(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function encodedSize(value: string) {
  try {
    return encodeURIComponent(value).length;
  } catch {
    return Infinity;
  }
}

export function validClickId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && encodedSize(value) <= 350;
}

function fresh(at: unknown, now: number): at is number {
  return (
    typeof at === "number" &&
    Number.isSafeInteger(at) &&
    at > 0 &&
    at <= now &&
    now - at <= ATTRIBUTION_MAX_AGE_MS
  );
}

export function entryLabels(value: EntryLabels): EntryLabels | undefined {
  const entry: EntryLabels = {};
  for (const key of ["source", "campaign", "medium"] as const) {
    const field = value[key];
    if (typeof field === "string" && field && encodedSize(field) <= 150) entry[key] = field;
  }
  return Object.keys(entry).length ? entry : undefined;
}

export function parseAttributionStorage(raw: unknown, now = Date.now()): AttributionStorage {
  const result: AttributionStorage = { v: 1 };
  if (
    typeof raw !== "string" ||
    raw.length > ATTRIBUTION_MAX_ENCODED_SIZE ||
    encodedSize(raw) > ATTRIBUTION_MAX_ENCODED_SIZE
  )
    return result;
  try {
    const data: unknown = JSON.parse(raw);
    if (!object(data) || data.v !== 1) return result;
    for (const key of CLICK_KEYS) {
      const click = data[key];
      if (object(click) && validClickId(click.id) && fresh(click.at, now))
        result[key] = { id: click.id, at: click.at };
    }
    if (object(data.paid)) {
      const at = Number(data.paid.ads_captured_at);
      // Campaign metadata lasts 90 days; the unchanged OpenAI parser still gates output at 30.
      const paid = fresh(at, now) ? parseAdsAttribution(data.paid, at) : undefined;
      if (paid && encodedSize(JSON.stringify(paid)) <= 1500) {
        result.paid = paid;
      }
      if (result.paid && !parseAdsAttribution(result.paid, now)) {
        if (result.paid.ads_source === "chatgpt_ads" || result.paid.ads_oppref) delete result.paid;
      }
    }
    if (object(data.entry) && fresh(data.entry.at, now)) {
      const entry = entryLabels(data.entry);
      if (entry) result.entry = { ...entry, at: data.entry.at };
    }
  } catch {
    // Invalid browser input is an empty record, never a checkout error.
  }
  return result;
}

export function captureAttributionStorage(
  current: AttributionStorage,
  search: string,
  now = Date.now()
): AttributionStorage {
  const next = { ...current };
  const params = new URLSearchParams(search);
  for (const key of CLICK_KEYS) {
    const id = params.get(key);
    // Repeated effects/reloads of the same click must not renew its lifetime.
    if (validClickId(id) && current[key]?.id !== id) next[key] = { id, at: now };
  }
  const paid = paidTouchFromUrl(search, now);
  if (paid) {
    if (encodedSize(JSON.stringify(paid)) <= 1500) {
      const previous = current.paid;
      const sameCampaign =
        previous &&
        JSON.stringify({ ...previous, ads_captured_at: "" }) ===
          JSON.stringify({ ...paid, ads_captured_at: "" });
      const newClick = CLICK_KEYS.some((key) => next[key] !== current[key]);
      next.paid = sameCampaign && !newClick ? previous : paid;
    }
  }
  return next;
}

export function serializeAttributionStorage(record: AttributionStorage): string | undefined {
  const raw = JSON.stringify(record);
  return encodedSize(raw) <= ATTRIBUTION_MAX_ENCODED_SIZE ? raw : undefined;
}

export function mergeAttributionStorage(
  current: AttributionStorage,
  incoming: AttributionStorage
): AttributionStorage {
  const next = { ...current };
  for (const key of CLICK_KEYS) {
    const click = incoming[key];
    if (click && click.at > (current[key]?.at ?? 0)) next[key] = click;
  }
  if (
    incoming.paid &&
    Number(incoming.paid.ads_captured_at) > Number(current.paid?.ads_captured_at ?? 0)
  ) {
    next.paid = incoming.paid;
  }
  return next;
}
